import { v4 } from 'uuid';
import { AnnotationPlane } from '../annotation/annotation';
import { DicomColor } from '../dicom/dicomColor';
import { FrameTransform } from '../registration/frameTransform';
import {
    euclideanDistanceMm,
    PointMm,
    toPointMm,
} from '../geometry/patientGeometry';

// `uuid`, not `crypto`: crypto is not always available at runtime.
export function createLandmarkPairId(): string {
    return `landmark-${v4()}`;
}

export interface LandmarkPoint {
    readonly pointMm: PointMm;
    readonly plane: AnnotationPlane;
    readonly frameOfReferenceUid: string;
}

export interface LandmarkPair {
    readonly id: string;
    readonly fixed?: LandmarkPoint;
    readonly moving?: LandmarkPoint;
    readonly visible: boolean;
    readonly name?: string;
    readonly color?: DicomColor;
}

export const LANDMARK_NAME_MAX_LENGTH = 64;

export function landmarkDisplayName(
    pair: LandmarkPair,
    ordinal: number,
    numbered: (ordinal: number) => string
): string {
    const given = pair.name?.trim();
    return given ? given : numbered(ordinal);
}

export interface CompleteLandmarkPair extends LandmarkPair {
    readonly fixed: LandmarkPoint;
    readonly moving: LandmarkPoint;
}

export function isCompleteLandmarkPair(
    pair: LandmarkPair
): pair is CompleteLandmarkPair {
    return pair.fixed !== undefined && pair.moving !== undefined;
}

export type LandmarkResidual =
    | {
          readonly pairId: string;
          readonly status: 'measured';
          readonly residualMm: number;
      }
    | {
          readonly pairId: string;
          readonly status: 'outside-field';
      }
    | {
          readonly pairId: string;
          readonly status: 'same-image';
      }
    | {
          readonly pairId: string;
          readonly status: 'other-frames';
      };

export interface LandmarkFrames {
    readonly fixed: string;
    readonly moving: string;
}

export function landmarkResidual(
    pair: CompleteLandmarkPair,
    transform: FrameTransform,
    frames?: LandmarkFrames
): LandmarkResidual {
    // A point from another registration's scans is not this transform's error.
    if (
        frames &&
        (pair.fixed.frameOfReferenceUid !== frames.fixed ||
            pair.moving.frameOfReferenceUid !== frames.moving)
    ) {
        return { pairId: pair.id, status: 'other-frames' };
    }
    if (
        pair.fixed.plane.seriesInstanceUid ===
        pair.moving.plane.seriesInstanceUid
    ) {
        return { pairId: pair.id, status: 'same-image' };
    }
    const mapped = transform.forward(pair.fixed.pointMm);
    if (!mapped) {
        return { pairId: pair.id, status: 'outside-field' };
    }
    return {
        pairId: pair.id,
        status: 'measured',
        residualMm: euclideanDistanceMm(toPointMm(mapped), pair.moving.pointMm),
    };
}

export function landmarkResiduals(
    pairs: readonly LandmarkPair[],
    transform: FrameTransform,
    frames?: LandmarkFrames
): LandmarkResidual[] {
    return pairs
        .filter(isCompleteLandmarkPair)
        .map((pair) => landmarkResidual(pair, transform, frames));
}

export function measuredResidualsMm(
    residuals: readonly LandmarkResidual[]
): number[] {
    return residuals.flatMap((residual) =>
        residual.status === 'measured' ? [residual.residualMm] : []
    );
}

export function provisionalLandmarkResidual(
    pair: LandmarkPair,
    half: 'fixed' | 'moving',
    pointMm: PointMm,
    transform: FrameTransform
): LandmarkResidual | undefined {
    const moved = pair[half];
    if (!moved) {
        return undefined;
    }
    const provisional = { ...pair, [half]: { ...moved, pointMm } };
    return isCompleteLandmarkPair(provisional)
        ? landmarkResidual(provisional, transform)
        : undefined;
}
